"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"
import { DIALOG_FOOTER_CLASS, DIALOG_HEADER_CLASS, DIALOG_TITLE_CLASS, dialogPadClass, splitHeaderClass, titleLayoutClass } from "@/components/ui/dialog-standard"

function AlertDialog({
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
    return <DialogPrimitive.Root data-slot="alert-dialog" {...props} />;
}

function AlertDialogTrigger({
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
    return <DialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />;
}

function AlertDialogPortal({
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
    return <DialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />;
}

function AlertDialogOverlay({
    className,
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
    return (
        <DialogPrimitive.Overlay
            data-slot="alert-dialog-overlay"
            className={cn(
                "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
                className
            )}
            {...props} />
    );
}

function AlertDialogContent({
    className,
    children,
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Content>) {
    const hasCustomMaxWidth = typeof className === "string" && /(?:^|\s)(?:[a-z0-9_-]+:)?max-w-/.test(className);

    return (
        <AlertDialogPortal>
            <AlertDialogOverlay />
            <DialogPrimitive.Content
                data-slot="alert-dialog-content"
                className={cn(
                    "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 outline-none",
                    !hasCustomMaxWidth && "sm:max-w-lg",
                    dialogPadClass(className),
                    className
                )}
                {...props}>
                {children}
            </DialogPrimitive.Content>
        </AlertDialogPortal>
    );
}

/** Header chuẩn EMS (cùng dialog.tsx): nền trắng sát mép hộp, viền dưới */
function AlertDialogHeader({
    className,
    children,
    ...props
}: React.ComponentProps<"div">) {
    const { outer, inner } = splitHeaderClass(className);
    return (
        <div data-slot="alert-dialog-header" className={cn("text-left", DIALOG_HEADER_CLASS, outer)} {...props}>
            <div className={cn("flex min-w-0 flex-col gap-1", inner)}>{children}</div>
        </div>
    );
}

function AlertDialogFooter({
    className,
    ...props
}: React.ComponentProps<"div">) {
    return (
        <div
            data-slot="alert-dialog-footer"
            className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", DIALOG_FOOTER_CLASS, className)}
            {...props} />
    );
}

function AlertDialogTitle({
    className,
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
    return (
        <DialogPrimitive.Title
            data-slot="alert-dialog-title"
            className={cn(DIALOG_TITLE_CLASS, titleLayoutClass(className))}
            {...props} />
    );
}

function AlertDialogDescription({
    className,
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
    return (
        <DialogPrimitive.Description
            data-slot="alert-dialog-description"
            className={cn("text-muted-foreground text-sm", className)}
            {...props} />
    );
}

function AlertDialogAction({
    className,
    ...props
}: React.ComponentProps<"button">) {
    return (
        <button
            data-slot="alert-dialog-action"
            className={cn(buttonVariants(), className)}
            {...props} />
    );
}

function AlertDialogCancel({
    className,
    ...props
}: React.ComponentProps<"button">) {
    return (
        <DialogPrimitive.Close asChild>
            <button
                data-slot="alert-dialog-cancel"
                className={cn(buttonVariants({ variant: "outline" }), className)}
                {...props} />
        </DialogPrimitive.Close>
    );
}

export {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    AlertDialogPortal,
    AlertDialogTitle,
    AlertDialogTrigger,
}
